import { Filter, Search, SlidersHorizontal } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@vnetwork/ui/primitives';
import {
  FilterBuilder,
  type ColumnDef,
  type FilterCondition,
  type FilterField,
} from '@vnetwork/ui/data';
import type { CustomerListRow } from '@/api/customers';

export type SectionColumn = ColumnDef<CustomerListRow, unknown>;

/** Resolve a column's stable toggle key (id, falling back to a string header). */
export const colId = (c: SectionColumn): string =>
  typeof c.id === 'string' ? c.id : String(c.header ?? '');

export interface CustomerListToolbarProps {
  columns: SectionColumn[];
  filterFields?: FilterField[];
  searchPlaceholder: string;
  query: string;
  onQueryChange: (value: string) => void;
  conditions: FilterCondition[];
  filterOpen: boolean;
  onFilterOpenChange: (open: boolean) => void;
  onApplyFilters: (next: FilterCondition[]) => void;
  visible: Record<string, boolean>;
  onToggleColumn: (id: string, checked: boolean) => void;
}

/**
 * The list toolbar: a server-side search `Input` (start) plus the Filters
 * `Popover` → `FilterBuilder` and a Display-options column-visibility
 * `DropdownMenu` (end). Purely presentational — all state lives in the parent
 * `CustomerListSection`.
 */
export function CustomerListToolbar({
  columns,
  filterFields,
  searchPlaceholder,
  query,
  onQueryChange,
  conditions,
  filterOpen,
  onFilterOpenChange,
  onApplyFilters,
  visible,
  onToggleColumn,
}: CustomerListToolbarProps) {
  // Conditions fold into a `{ field: [...] }` filters object, so the badge should
  // reflect how many DISTINCT fields are constrained, not the raw condition count.
  const appliedFieldCount = new Set(conditions.map((c) => c.field)).size;

  return (
    <Toolbar
      style={{ marginBottom: 0 }}
      start={
        <Input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={searchPlaceholder}
          startSlot={<Search />}
          style={{ maxWidth: 320 }}
        />
      }
      end={
        <>
          {filterFields?.length ? (
            <Popover open={filterOpen} onOpenChange={onFilterOpenChange}>
              <PopoverTrigger asChild>
                <Button variant="secondary" startIcon={<Filter size={15} />}>
                  Filters{' '}
                  {appliedFieldCount ? <Badge tone="primary">{appliedFieldCount}</Badge> : null}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" style={{ padding: 0 }}>
                <FilterBuilder
                  fields={filterFields}
                  defaultValue={conditions.length ? conditions : undefined}
                  onApply={onApplyFilters}
                  onClose={() => onFilterOpenChange(false)}
                />
              </PopoverContent>
            </Popover>
          ) : null}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                Display options
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
              {columns.map((c) => {
                const id = colId(c);
                return (
                  <DropdownMenuCheckboxItem
                    key={id}
                    checked={visible[id] !== false}
                    onCheckedChange={(v) => onToggleColumn(id, v === true)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {typeof c.header === 'string' ? c.header : id}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      }
    />
  );
}
